import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { assistantService } from "../services/assistantService";
import {
  AdvisorUser,
  AssistantCaseListItem,
  AssistantPaginationMeta,
  AssistantUser,
  ConsultationBookingPayload,
  CreateLeadPayload,
  LeadConversionResponse,
  LeadListItem,
} from "../types/assistant.types";

interface UseAssistantDashboardResult {
  assistant: AssistantUser | null;
  advisors: AdvisorUser[];
  leads: LeadListItem[];
  cases: AssistantCaseListItem[];
  leadPagination: AssistantPaginationMeta | null;
  casePagination: AssistantPaginationMeta | null;
  loading: boolean;
  error: string | null;
  nextLeadPage: () => void;
  prevLeadPage: () => void;
  nextCasePage: () => void;
  prevCasePage: () => void;
  refresh: () => Promise<void>;
  createLead: (payload: Omit<CreateLeadPayload, "firm_id" | "assigned_assistant_id">) => Promise<void>;
  bookConsultation: (leadId: string, payload: ConsultationBookingPayload) => Promise<void>;
  sendEngagementLetter: (leadId: string) => Promise<void>;
  convertLeadToClient: (leadId: string) => Promise<LeadConversionResponse>;
}

interface AssistantQueryState {
  lead_limit: number;
  lead_offset: number;
  case_limit: number;
  case_offset: number;
}

const defaultQuery: AssistantQueryState = {
  lead_limit: 5,
  lead_offset: 0,
  case_limit: 5,
  case_offset: 0,
};

const toErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
};

export const useAssistantDashboard = (): UseAssistantDashboardResult => {
  const { user } = useAuth();
  const [assistant, setAssistant] = useState<AssistantUser | null>(null);
  const [advisors, setAdvisors] = useState<AdvisorUser[]>([]);
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [cases, setCases] = useState<AssistantCaseListItem[]>([]);
  const [leadPagination, setLeadPagination] = useState<AssistantPaginationMeta | null>(null);
  const [casePagination, setCasePagination] = useState<AssistantPaginationMeta | null>(null);
  const [query, setQuery] = useState<AssistantQueryState>(defaultQuery);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const assistantId = user?.id;

  const load = useCallback(async () => {
    if (!assistantId) {
      setError("Assistant session is missing.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const assistantUser = await assistantService.getAssistant(assistantId);
      const [leadData, caseData, advisorData] = await Promise.all([
        assistantService.listLeads({
          limit: query.lead_limit,
          offset: query.lead_offset,
        }),
        assistantService.listCases({
          limit: query.case_limit,
          offset: query.case_offset,
        }),
        assistantService.listAdvisors(assistantUser.firm_id),
      ]);

      setAssistant(assistantUser);
      setLeads(leadData.leads);
      setCases(caseData.cases);
      setLeadPagination(leadData.pagination);
      setCasePagination(caseData.pagination);
      setAdvisors(advisorData);
    } catch (err) {
      setError(toErrorMessage(err, "Could not load assistant dashboard data."));
    } finally {
      setLoading(false);
    }
  }, [assistantId, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const createLead = useCallback(
    async (payload: Omit<CreateLeadPayload, "firm_id" | "assigned_assistant_id">) => {
      if (!assistantId || !assistant) {
        throw new Error("Assistant session is missing.");
      }

      try {
        await assistantService.createLead({
          ...payload,
          firm_id: assistant.firm_id,
          assigned_assistant_id: assistantId,
        });
        await load();
      } catch (err) {
        throw new Error(toErrorMessage(err, "Could not create lead."));
      }
    },
    [assistant, assistantId, load]
  );

  const bookConsultation = useCallback(
    async (leadId: string, payload: ConsultationBookingPayload) => {
      try {
        await assistantService.bookConsultation(leadId, payload);
        await load();
      } catch (err) {
        throw new Error(toErrorMessage(err, "Could not book consultation."));
      }
    },
    [load]
  );

  const sendEngagementLetter = useCallback(
    async (leadId: string) => {
      try {
        await assistantService.sendEngagementLetter(leadId);
        await load();
      } catch (err) {
        throw new Error(toErrorMessage(err, "Could not send engagement letter."));
      }
    },
    [load]
  );

  const convertLeadToClient = useCallback(
    async (leadId: string): Promise<LeadConversionResponse> => {
      try {
        const result = await assistantService.convertLeadToClient(leadId);
        await load();
        return result;
      } catch (err) {
        throw new Error(toErrorMessage(err, "Could not convert lead to client."));
      }
    },
    [load]
  );

  const nextLeadPage = useCallback(() => {
    if (!leadPagination?.has_more) return;
    setQuery((prev) => ({
      ...prev,
      lead_offset: prev.lead_offset + prev.lead_limit,
    }));
  }, [leadPagination?.has_more]);

  const prevLeadPage = useCallback(() => {
    if (!leadPagination || leadPagination.offset === 0) return;
    setQuery((prev) => ({
      ...prev,
      lead_offset: Math.max(0, prev.lead_offset - prev.lead_limit),
    }));
  }, [leadPagination]);

  const nextCasePage = useCallback(() => {
    if (!casePagination?.has_more) return;
    setQuery((prev) => ({
      ...prev,
      case_offset: prev.case_offset + prev.case_limit,
    }));
  }, [casePagination?.has_more]);

  const prevCasePage = useCallback(() => {
    if (!casePagination || casePagination.offset === 0) return;
    setQuery((prev) => ({
      ...prev,
      case_offset: Math.max(0, prev.case_offset - prev.case_limit),
    }));
  }, [casePagination]);

  return {
    assistant,
    advisors,
    leads,
    cases,
    leadPagination,
    casePagination,
    loading,
    error,
    nextLeadPage,
    prevLeadPage,
    nextCasePage,
    prevCasePage,
    refresh: load,
    createLead,
    bookConsultation,
    sendEngagementLetter,
    convertLeadToClient,
  };
};
